// Records a voice message in the browser (docs/web-konzept.md, decision 13): the microphone at
// 48 kHz mono through an AudioWorklet, Opus from WebCodecs' AudioEncoder (24 kbit/s, 20 ms frames
// like the app), packed into Ogg; the waveform is one byte (peak × 255) per started 100 ms.

import { OggOpusWriter, preSkipOf } from "./ogg.ts";

export const SAMPLE_RATE = 48_000;
/** The app's limit (AudioPolicy.MAX_DURATION_MS): ten minutes. */
export const MAX_DURATION_MS = 10 * 60 * 1000;
const WAVE_SAMPLES = SAMPLE_RATE / 10;

export interface Recording {
  ogg: Uint8Array<ArrayBuffer>;
  durationMs: number;
  waveform: Uint8Array<ArrayBuffer>;
}

/** Whether this browser can record voice messages at all. */
export async function canRecord(): Promise<boolean> {
  if (typeof AudioEncoder === "undefined" || !navigator.mediaDevices?.getUserMedia || typeof AudioWorkletNode === "undefined") return false;
  try {
    return (await AudioEncoder.isConfigSupported(encoderConfig())).supported === true;
  } catch {
    return false;
  }
}

function encoderConfig(): AudioEncoderConfig {
  return { codec: "opus", sampleRate: SAMPLE_RATE, numberOfChannels: 1, bitrate: 24_000, opus: { frameDuration: 20_000, application: "voip" } } as AudioEncoderConfig;
}

export class Recorder {
  private context: AudioContext | null = null;
  private stream: MediaStream | null = null;
  private node: AudioWorkletNode | null = null;
  private encoder: AudioEncoder | null = null;
  private packets: { data: Uint8Array; samples: number }[] = [];
  private preSkip: number | undefined;
  private wave: number[] = [];
  private wavePeak = 0;
  private waveCount = 0;
  private samples = 0;
  private paused = false;
  private error: Error | null = null;
  /** Called with each 20 ms block's level (0..1) and the duration so far; for the UI. */
  onLevel: (level: number, durationMs: number) => void = () => undefined;
  /** Called with each 20 ms block as 16 bit PCM at 48 kHz (for the live transcript). */
  onPcm: ((pcm: Float32Array) => void) | null = null;
  /** Each Opus packet as it comes from the encoder (20 ms; for live listening). */
  onPacket: ((packet: Uint8Array) => void) | null = null;
  /** The ten minutes are over. */
  onLimit: () => void = () => undefined;

  get durationMs(): number {
    return Math.round((this.samples * 1000) / SAMPLE_RATE);
  }

  /** Asks for the microphone (the browser shows its prompt the first time) and starts. */
  async start(deviceId?: string): Promise<void> {
    this.stream = await navigator.mediaDevices.getUserMedia({
      audio: { channelCount: 1, echoCancellation: true, noiseSuppression: true, autoGainControl: true, ...(deviceId ? { deviceId: { exact: deviceId } } : {}) },
    });
    this.context = new AudioContext({ sampleRate: SAMPLE_RATE });
    await this.context.audioWorklet.addModule("/recorder-worklet.js");
    this.encoder = new AudioEncoder({
      output: (chunk, metadata) => {
        this.preSkip ??= preSkipOf(metadata?.decoderConfig?.description);
        const data = new Uint8Array(chunk.byteLength);
        chunk.copyTo(data);
        this.packets.push({ data, samples: Math.round(((chunk.duration ?? 20_000) * SAMPLE_RATE) / 1_000_000) });
        this.onPacket?.(data);
      },
      error: (e) => (this.error = e as Error),
    });
    this.encoder.configure(encoderConfig());
    const source = this.context.createMediaStreamSource(this.stream);
    this.node = new AudioWorkletNode(this.context, "voxie-recorder");
    this.node.port.onmessage = (event: MessageEvent<Float32Array>) => this.block(event.data);
    source.connect(this.node);
  }

  pause() {
    this.paused = true;
  }

  resume() {
    this.paused = false;
  }

  get isPaused() {
    return this.paused;
  }

  /** Stops and returns the finished recording. */
  async stop(): Promise<Recording> {
    this.release();
    const encoder = this.encoder!;
    await encoder.flush();
    encoder.close();
    if (this.error) throw this.error;
    if (this.waveCount > 0) this.wave.push(Math.round(this.wavePeak * 255));
    const writer = new OggOpusWriter({ preSkip: this.preSkip ?? 312 });
    for (const p of this.packets) writer.add(p.data, p.samples);
    const durationMs = this.durationMs;
    return { ogg: writer.finish() as Uint8Array<ArrayBuffer>, durationMs, waveform: new Uint8Array(this.wave) };
  }

  /** Throws the recording away. */
  cancel() {
    this.release();
    try {
      this.encoder?.close();
    } catch {
      // Already closed.
    }
  }

  private block(pcm: Float32Array) {
    if (this.paused || !this.encoder || this.encoder.state !== "configured") return;
    if (this.durationMs >= MAX_DURATION_MS) {
      this.onLimit();
      return;
    }
    this.encoder.encode(new AudioData({ format: "f32", sampleRate: SAMPLE_RATE, numberOfChannels: 1, numberOfFrames: pcm.length, timestamp: (this.samples * 1_000_000) / SAMPLE_RATE, data: pcm as Float32Array<ArrayBuffer> }));
    let peak = 0;
    for (const s of pcm) {
      const a = Math.min(1, Math.abs(s));
      if (a > peak) peak = a;
      this.wavePeak = Math.max(this.wavePeak, a);
      if (++this.waveCount === WAVE_SAMPLES) {
        this.wave.push(Math.round(this.wavePeak * 255));
        this.wavePeak = 0;
        this.waveCount = 0;
      }
    }
    this.samples += pcm.length;
    this.onPcm?.(pcm);
    this.onLevel(peak, this.durationMs);
  }

  private release() {
    this.node?.port.close();
    this.node?.disconnect();
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    void this.context?.close().catch(() => undefined);
    this.node = null;
    this.stream = null;
    this.context = null;
  }
}

